/**
 * User Avatar Component
 *
 * @module modules/user/presentation/shared/components/UserAvatar
 *
 * Displays user avatar with initials fallback.
 * Reusable across different parts of the application.
 */

import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import { memo } from 'react';

import type { UserAvatarProps } from '../types';

/**
 * Get user initials from name
 */
function getUserInitials(name?: string): string {
  if (!name) return 'U';
  const names = name.split(' ');
  if (names.length >= 2) {
    return `${names[0][0]}${names[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/**
 * User Avatar Component
 *
 * Displays user avatar with gradient background and initials fallback.
 *
 * @example
 * ```tsx
 * <UserAvatar name="John Doe" avatarUrl="/avatar.jpg" size={40} />
 * ```
 */
export const UserAvatar = memo(function UserAvatar({
  name,
  avatarUrl,
  size = 32,
  showTooltip = false,
  tooltipText,
}: UserAvatarProps) {
  const initials = getUserInitials(name);
  const displayTooltip = tooltipText || name || 'User';

  const avatar = (
    <Avatar
      {...(avatarUrl !== undefined && { src: avatarUrl })}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.375,
        fontWeight: 600,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      {initials}
    </Avatar>
  );

  if (showTooltip) {
    return (
      <Tooltip title={displayTooltip} placement="right" arrow>
        {avatar}
      </Tooltip>
    );
  }

  return avatar;
});

